import { ScraperData, DailyScrape, FocusTableData } from '../../types'
import { realUrlCounts } from './realScraperData'

// Generate dates for the last 28 days
const generateDates = () => {
  const dates = []
  const today = new Date()
  for (let i = 27; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    dates.push(
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    )
  }
  return dates
}

// Use the first 10 scrapers for the mock data
const generateScrapers = () => {
  // Get the first 10 scrapers with real URL counts
  return realUrlCounts.slice(0, 10)
}

// Generate mock daily data with more realistic patterns
const generateDailyData = () => {
  const dates = generateDates()
  const dailyData: DailyScrape[] = []
  const scrapers = generateScrapers()
  // Define which AM/PM runs should be entirely grey (data unavailable)
  // Format: [dateIndex, isAM] - e.g. [5, true] means the AM run of the 6th day
  const unavailableRuns = [
    [3, true], // 4th day AM
    [18, false], // 19th day PM
  ]
  // Create scheduled events for each scraper
  // Each event has a start day, duration, and magnitude
  const scraperEvents: {
    [key: number]: Array<{
      startDay: number
      duration: number
      magnitude: number
      isNegative?: boolean
      isBroken?: boolean
    }>
  } = {}
  // Initialize each scraper with mostly stable data and a few planned events
  scrapers.forEach((scraper) => {
    // Each scraper gets 1-3 events over the 28 day period
    const numEvents = Math.floor(Math.random() * 3) + 1
    const events = []
    for (let i = 0; i < numEvents; i++) {
      // Events are more likely to be minor than significant
      const magnitude =
        Math.random() < 0.7
          ? Math.floor(Math.random() * 15) + 5 // 5-20 changes (routine to moderate)
          : Math.floor(Math.random() * 30) + 20 // 20-50 changes (moderate to significant)
      // Most events are brief
      const duration = Math.floor(Math.random() * 2) + 1 // 1-2 days
      // Events are distributed throughout the month
      const startDay = Math.floor(Math.random() * 25) // Ensures event fits within 28 days
      // 30% chance of negative change, 70% positive
      const isNegative = Math.random() < 0.3
      // 5% chance of broken status (only for negative events)
      const isBroken = isNegative && Math.random() < 0.05
      events.push({
        startDay,
        duration,
        magnitude,
        isNegative,
        isBroken,
      })
    }
    scraperEvents[scraper.id] = events
  })
  // Create one special broken scraper that gets fixed after 2 days
  const brokenScraperId =
    scrapers[Math.floor(Math.random() * scrapers.length)].id
  const brokenStartDay = Math.floor(Math.random() * 25)
  scraperEvents[brokenScraperId].push({
    startDay: brokenStartDay,
    duration: 2,
    magnitude: 95, // 95% drop
    isNegative: true,
    isBroken: true,
  })
  // Generate data for each day
  dates.forEach((date, dateIndex) => {
    const amData: ScraperData[] = []
    const pmData: ScraperData[] = []
    // Check if this run should be unavailable
    const amUnavailable = unavailableRuns.some(
      (run) => run[0] === dateIndex && run[1] === true,
    )
    const pmUnavailable = unavailableRuns.some(
      (run) => run[0] === dateIndex && run[1] === false,
    )
    // Process each scraper
    for (let i = 0; i < scrapers.length; i++) {
      const scraper = scrapers[i]
      const baseUrls = scraper.urlCount
      // Default to stable (0 change)
      let amDelta = 0
      let pmDelta = 0
      // Check if there's an active event for this scraper on this day
      const activeEvents = scraperEvents[scraper.id].filter(
        (event) =>
          dateIndex >= event.startDay &&
          dateIndex < event.startDay + event.duration,
      )
      if (activeEvents.length > 0) {
        // Apply the most significant event if there are multiple
        const event = activeEvents.sort((a, b) => b.magnitude - a.magnitude)[0]
        // Calculate delta based on event magnitude as percentage of base URLs
        const deltaMagnitude = Math.floor(baseUrls * (event.magnitude / 100))
        // Apply delta (positive or negative)
        const eventDelta = event.isNegative ? -deltaMagnitude : deltaMagnitude
        // For broken status, make a large negative delta
        if (event.isBroken) {
          amDelta = -Math.floor(baseUrls * 0.95)
          pmDelta = -Math.floor(baseUrls * 0.95)
        } else {
          // Small variation between AM and PM
          const variation = Math.floor(Math.random() * 5) // 0-4 variation
          amDelta = eventDelta
          pmDelta = eventDelta + (Math.random() > 0.5 ? variation : -variation)
        }
      } else {
        // No active event - mostly stable with small random variations
        if (Math.random() < 0.8) {
          // 80% chance of being completely stable
          amDelta = 0
          pmDelta = 0
        } else {
          // 20% chance of minor change
          amDelta = Math.floor(Math.random() * 7) - 3 // -3 to +3
          pmDelta = Math.floor(Math.random() * 7) - 3 // -3 to +3
        }
      }
      // Add data if run is available
      if (!amUnavailable) {
        amData.push({
          id: scraper.id,
          name: scraper.name,
          urlsScraped: Math.max(0, baseUrls + amDelta), // Prevent negative URL count
          status: getStatusFromDelta(amDelta, baseUrls),
          amPm: 'AM',
          date,
          delta: amDelta,
          previousUrls: baseUrls,
        })
      }
      if (!pmUnavailable) {
        pmData.push({
          id: scraper.id,
          name: scraper.name,
          urlsScraped: Math.max(0, baseUrls + pmDelta), // Prevent negative URL count
          status: getStatusFromDelta(pmDelta, baseUrls),
          amPm: 'PM',
          date,
          delta: pmDelta,
          previousUrls: baseUrls,
        })
      }
    }
    dailyData.push({
      date,
      am: amData,
      pm: pmData,
    })
  })
  return dailyData
}

// Helper function to determine status based on delta
function getStatusFromDelta(delta: number, baseUrls: number) {
  const absDelta = Math.abs(delta)
  const percentChange = (absDelta / baseUrls) * 100
  if (delta < 0 && percentChange >= 90) {
    return 'broken'
  }
  if (absDelta >= 50) {
    return 'needsAttention'
  }
  if (absDelta >= 40 && absDelta <= 49) {
    return 'major'
  }
  if (absDelta >= 30 && absDelta <= 39) {
    return 'moderate'
  }
  if (absDelta >= 20 && absDelta <= 29) {
    return 'minor'
  }
  if (absDelta >= 10 && absDelta <= 19) {
    return 'routine'
  }
  if (absDelta >= 1 && absDelta <= 9) {
    return 'minimal'
  }
  return 'stable'
}

// Generate focus table data with updated status categories
const generateFocusTableData = () => {
  const positive = [
    {
      scraperId: 51,
      scraperName: '051 BCBS Tennessee',
      urlChange: 52,
      percentChange: 650,
      previousCount: 8,
      currentCount: 60,
      status: 'needsAttention',
      amPm: 'AM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 39,
      scraperName: '039 AlohaCare',
      urlChange: 35,
      percentChange: 39,
      previousCount: 90,
      currentCount: 125,
      status: 'moderate',
      amPm: 'PM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 106,
      scraperName: '106 Centene Corporation',
      urlChange: 25,
      percentChange: 20,
      previousCount: 1118,
      currentCount: 1143,
      status: 'minor',
      amPm: 'AM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 45,
      scraperName: '045 BCBS Louisiana',
      urlChange: 15,
      percentChange: 15,
      previousCount: 915,
      currentCount: 930,
      status: 'routine',
      amPm: 'PM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 4,
      scraperName: '004 BCBS Arkansas',
      urlChange: 8,
      percentChange: 5,
      previousCount: 890,
      currentCount: 898,
      status: 'minimal',
      amPm: 'AM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
  ]
  const negative = [
    {
      scraperId: 97,
      scraperName: '097 Neighborhood Health RI',
      urlChange: 1342,
      percentChange: 95,
      previousCount: 1413,
      currentCount: 71,
      status: 'broken',
      amPm: 'PM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 82,
      scraperName: '082 BCBS Highmark',
      urlChange: 718,
      percentChange: 50,
      previousCount: 1436,
      currentCount: 718,
      status: 'needsAttention',
      amPm: 'AM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 11,
      scraperName: '011 BCBS Michigan',
      urlChange: 45,
      percentChange: 40,
      previousCount: 1200,
      currentCount: 1155,
      status: 'major',
      amPm: 'PM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 118,
      scraperName: '118 TriCare',
      urlChange: 35,
      percentChange: 30,
      previousCount: 2099,
      currentCount: 2064,
      status: 'moderate',
      amPm: 'AM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
    {
      scraperId: 28,
      scraperName: '028 BCBS Federal Employees (FEP)',
      urlChange: 25,
      percentChange: 25,
      previousCount: 996,
      currentCount: 971,
      status: 'minor',
      amPm: 'PM',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    },
  ]
  return { positive, negative }
}

export const mockScraperData = {
  scrapers: generateScrapers(),
  dailyData: generateDailyData(),
}

export const mockFocusTableData = generateFocusTableData()
